import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type FormValidationContextProps = {
  fieldsValidation: FieldValidation[];
  isValidForm: boolean;
  updateFieldValidation: (fieldKey: string, isValid: boolean) => void;
  removeFormValidation: (formId: number) => void;
  scrollToError: () => void;
  showError: boolean;
};

export const FormValidationContext = createContext<FormValidationContextProps>({} as FormValidationContextProps);

type FormValidationProviderProps = {
  children: React.ReactNode;
};

type FieldValidation = {
  fieldKey: string;
  isValid: boolean;
};

export const FormValidationProvider: React.FC<FormValidationProviderProps> = ({ children }) => {
  const [fields, setFields] = useState<FieldValidation[]>([]);
  const [showError, setShowError] = useState<boolean>(false);

  const isValidForm = useMemo(() => {
    return fields.every(field => field.isValid);
  }, [fields]);

  const updateFieldValidation = useCallback(
    (fieldKey: string, isValid: boolean) => {
      console.info('updateFieldValidation', fieldKey, isValid);
      setFields(prevFields => {
        if (prevFields.find(field => field.fieldKey === fieldKey)) {
          return prevFields.map(fieldVal => {
            if (fieldVal.fieldKey === fieldKey) {
              fieldVal.isValid = isValid;
            }
            return fieldVal;
          });
        }
        return [...prevFields, { fieldKey: fieldKey, isValid: isValid }];
      });
    },
    [setFields]
  );

  const removeFormValidation = useCallback((formId: number) => {
    setFields(prevFields => {
      return prevFields
        .filter(field => field.fieldKey.split('-')[0] !== formId.toString())
        .map(field => {
          const fieldKeyParts = field.fieldKey.split('-');
          if (Number(fieldKeyParts[0]) > formId) {
            fieldKeyParts[0] = (Number(fieldKeyParts[0]) - 1).toString();
            field.fieldKey = fieldKeyParts.join('-');
          }
          return field;
        });
    });
  }, []);

  const firstInvalidFieldKey = useMemo(() => {
    return fields.find(field => !field.isValid)?.fieldKey;
  }, [fields]);

  const scrollToError = useCallback(() => {
    setShowError(true);
    if (firstInvalidFieldKey) {
      const element = document.getElementById(firstInvalidFieldKey);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 50;
        window.scrollTo({ behavior: 'smooth', top: offsetPosition });
      }
    }
  }, [firstInvalidFieldKey]);

  return (
    <FormValidationContext.Provider
      value={{
        fieldsValidation: fields,
        isValidForm,
        updateFieldValidation,
        removeFormValidation,
        scrollToError,
        showError,
      }}
    >
      {children}
    </FormValidationContext.Provider>
  );
};

export const useFormValidation = (): FormValidationContextProps => useContext(FormValidationContext);
